// Follow-ups owed today, and the button that drafts them.
//
// THE DRAFT BUTTON DOES NOT REIMPLEMENT DRAFTING. It runs DimeMotionOS's own run_fup.py, which
// carries the gates that were each added after a real incident: thread resolution and stale-id
// repair, a reply re-check at ship time, a prior-step-actually-sent check against Gmail, FUP4
// trailer dedup, greeting-name resolution, and the no-stamp-without-a-verified-draft-id rule.
// Drafts only, always: the service account has no send scope, so nothing leaves the mailbox
// without a human sending it.
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../services";
import type { Defined, FupDue, FupItem } from "../services/types";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { StatTiles } from "../components/dash/StatTiles";
import { DefinitionsPanel } from "../components/dash/DefinitionsPanel";
import { DataTable, type Column } from "../components/dash/DataTable";
import { ConfirmBar } from "../components/dash/ConfirmBar";
import { RunResults, useJobRunner } from "../components/dash/JobPanel";

const TILES = ["fup.total", "fup.sendable", "fup.blocked", "fup.skipped.replied_or_booked", "fup.skipped.no_audit"];

const COLUMNS: Column<FupItem>[] = [
  { key: "step", label: "Step", width: 64, cell: (i) => `FUP${i.step}` },
  { key: "overdue", label: "Overdue", width: 90, cell: (i) => (i.overdue_days > 0 ? `${i.overdue_days}d` : "today") },
  { key: "show", label: "Show", cell: (i) => i.channel },
  { key: "person", label: "Person", cell: (i) => <span title={i.email}>{i.person || i.email}</span> },
  { key: "sender", label: "Sender", cell: (i) => (i.sender || "").split("@")[0] || "none" },
  { key: "state", label: "State", width: 250, cell: (i) => (i.sendable ? <span className="ok">sendable</span> : i.blocker) },
];

export default function FollowUps() {
  const runner = useJobRunner();
  const [stepFilter, setStepFilter] = useState(0);
  const [onlyStopped, setOnlyStopped] = useState(false);
  const refreshing = useRef(false);

  const dueQ = useQuery({
    queryKey: ["followups:due"],
    queryFn: async () => {
      const r = await api.followups.dueToday(0, "", 0, refreshing.current);
      refreshing.current = false;
      if (!r.ok) throw new Error(r.error);
      return r as Defined<FupDue>;
    },
    staleTime: 120_000, // reads 1,400 Airtable rows; far too heavy to poll
  });

  const rows = useMemo(() => {
    const all = dueQ.data?.items ?? [];
    return all
      .filter((i) => (stepFilter ? i.step === stepFilter : true))
      .filter((i) => (onlyStopped ? !i.sendable : true));
  }, [dueQ.data, stepFilter, onlyStopped]);

  const d = dueQ.data;
  const scope = stepFilter ? `FUP${stepFilter}` : "all steps";
  const types = stepFilter ? String(stepFilter) : "1,2,3,4,5";

  function refresh() {
    refreshing.current = true;
    void dueQ.refetch();
  }

  async function dryRun() {
    await runner.run("Dry run", () => api.followups.dryRun(types));
  }

  async function createDrafts() {
    const r = await runner.run("Create drafts", () => api.followups.draft(types, true));
    if (r && !r.job) refresh();
  }

  return (
    <>
      <PageHead
        title="Follow-ups"
        caption="Owed today, read from Airtable. Drafting runs DimeMotionOS unchanged, so every gate still applies. Nothing here sends."
      />

      <section className="section compact">
        <SectionHead title="Today" />
        {dueQ.isError ? (
          <div className="note-neg"><p>{(dueQ.error as Error).message}</p></div>
        ) : !d ? (
          <p className="empty">Reading Airtable.</p>
        ) : (
          <>
            <StatTiles
              tiles={[
                { id: "fup.total", value: d.total },
                { id: "fup.sendable", value: d.sendable },
                { id: "fup.blocked", value: d.blocked },
                { id: "fup.skipped.replied_or_booked", value: d.skipped?.replied_or_booked },
                { id: "fup.skipped.no_audit", value: d.skipped?.no_audit },
              ]}
              definitions={d.definitions}
              readAt={d.read_at}
            />
            {d.blockers?.length ? (
              <div className="note">
                <p>
                  {d.blocked} of {d.total} are owed by a mailbox that cannot send them: {d.blockers.join("; ")}.
                  Listed rather than hidden, because each needs a decision: reassign the sender, or close
                  the record.
                </p>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="section compact">
        <SectionHead title="Run" />
        <div className="run">
          <button type="button" className="btn quiet" disabled={dueQ.isFetching || !!runner.busy} onClick={refresh}>
            {dueQ.isFetching ? "Reading" : "Refresh list"}
          </button>
          <button type="button" className="btn" disabled={!!runner.busy} onClick={dryRun}>
            Dry run ({scope})
          </button>
          <ConfirmBar
            label="Create drafts"
            confirmLabel={`Yes, draft ${scope} now`}
            note="Writes real Gmail drafts and stamps Airtable. It does not send."
            disabled={!!runner.busy || !d}
            busy={runner.busy === "Create drafts"}
            onConfirm={createDrafts}
          />
        </div>
        <RunResults runner={runner} onFinish={refresh} />
      </section>

      <section className="section compact">
        <SectionHead
          title="Queue"
          right={
            <div className="kws">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`kw${stepFilter === n ? " on" : ""}`}
                  onClick={() => setStepFilter(n)}
                >
                  {n === 0 ? `All ${d?.total ?? 0}` : `FUP${n} ${d?.by_step?.[String(n)] ?? 0}`}
                </button>
              ))}
              <button
                type="button"
                className={`kw${onlyStopped ? " on" : ""}`}
                onClick={() => setOnlyStopped((v) => !v)}
              >
                Sender stopped only
              </button>
            </div>
          }
        />
        <DataTable
          columns={COLUMNS}
          rows={rows}
          rowKey={(i) => i.record}
          loading={dueQ.isPending}
          loadingText="Reading all the Airtable records. The first load takes about 15 seconds; it is cached for 5 minutes after that."
          empty={d && d.total === 0 ? "Nothing owed today." : "Nothing owed under this filter."}
        />
      </section>

      {d ? <DefinitionsPanel ids={TILES} definitions={d.definitions} readAt={d.read_at} /> : null}
    </>
  );
}
